import { useEffect, useState } from "react";
import { ChevronRight, KeyRound, X } from "lucide-react";
import { logout } from "@/lib/api/auth";
import { getAuthToken, subscribeAuthStateChange } from "@/lib/api/token-store";
import { getProfiles, removeProfile, subscribeProfiles } from "@/lib/stores/profile-roster-store";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { LoginErrorDialog } from "@/components/layout/login-error-dialog";
import { FaceSignInStatus } from "@/components/auth/face-sign-in-status";
import { PinEntryDialog } from "@/components/auth/pin-entry-dialog";
import { maybePromptPinSetupAfterLogin } from "@/components/auth/pin-setup-dialog";
import { useLoginForm } from "@/hooks/use-login-form";
import { cn } from "@/lib/utils";

function initialsFor(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return parts
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");
}

// Lock-screen roster: the device's known profiles as tiles. PIN profiles open the PIN dialog;
// the rest reveal the credentials card prefilled with their loginId.
function ProfileRoster({ profiles, onPick, onRemove, onUseCredentials }) {
  return (
    <div className="space-y-2">
      <div className="overflow-hidden rounded-xl border border-border/70 bg-card shadow-lg shadow-primary/[0.07]">
        {profiles.map((profile) => (
          <div
            key={profile.userId}
            role="button"
            tabIndex={0}
            onClick={() => onPick(profile)}
            onKeyDown={(event) => {
              if (event.target !== event.currentTarget) return;
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onPick(profile);
              }
            }}
            className="flex w-full cursor-pointer items-center gap-3 border-b border-border/60 px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
          >
            <Avatar className="h-10 w-10 shrink-0">
              <AvatarFallback className="text-sm font-semibold">
                {initialsFor(profile.displayName)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-foreground">{profile.displayName}</p>
              <p className="truncate text-xs text-muted-foreground">{profile.loginId}</p>
            </div>
            {profile.hasPin ? (
              <KeyRound className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden title="Unlocks with PIN" />
            ) : (
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            )}
            <button
              type="button"
              className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-background hover:text-destructive"
              title="Remove from this device"
              aria-label={`Remove ${profile.displayName} from this device`}
              onClick={(event) => {
                event.stopPropagation();
                onRemove(profile);
              }}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
      <div className="text-center">
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto p-0 text-xs"
          onClick={onUseCredentials}
        >
          Sign in with another account
        </Button>
      </div>
    </div>
  );
}

function LoginPanel({ className, onLogoutSuccess, presentation = "sidebar" }) {
  const isPageLayout = presentation === "page";
  const [isLoggedIn, setIsLoggedIn] = useState(() => Boolean(getAuthToken()));
  const [profiles, setProfiles] = useState(() => getProfiles());
  // Page layout only: when the roster has tiles, they replace the credentials card until the
  // user asks for it (or picks a no-PIN profile, which prefills and reveals it).
  const [showCredentialsCard, setShowCredentialsCard] = useState(false);
  const [pinEntry, setPinEntry] = useState({ open: false, profile: null });

  const form = useLoginForm({
    onSuccess: (result, { password }) => {
      setIsLoggedIn(true);
      // First-login PIN offer (staff without a PIN). Rendered app-level: this panel lives in
      // the identity-keyed page tree and unmounts the instant the session lands.
      maybePromptPinSetupAfterLogin(result, password);
    },
  });

  useEffect(() => {
    const syncAuthState = () => {
      setIsLoggedIn(Boolean(getAuthToken()));
    };

    const unsubscribe = subscribeAuthStateChange(syncAuthState);
    return unsubscribe;
  }, []);

  useEffect(() => subscribeProfiles(setProfiles), []);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("[Auth] logout request failed:", error);
    }
    setIsLoggedIn(false);
    onLogoutSuccess?.();
  };

  const handlePickProfile = (profile) => {
    if (profile.hasPin) {
      setPinEntry({ open: true, profile });
      return;
    }
    form.setLoginId(profile.loginId);
    setShowCredentialsCard(true);
  };

  const showRoster = isPageLayout && !isLoggedIn && profiles.length > 0 && !showCredentialsCard;

  const credentialsCard = (
    <Card
      className={cn(
        "border-border/60 shadow-none",
        isPageLayout && "border-border/70 shadow-lg shadow-primary/[0.07]",
        className,
      )}
    >
      <CardHeader className={cn(isPageLayout ? "space-y-1.5 p-6 pb-0" : "space-y-0 p-3 pb-2")}>
        <CardTitle
          className={cn(
            isPageLayout
              ? "text-xl font-semibold tracking-tight text-foreground"
              : "text-xs font-medium uppercase tracking-wide text-muted-foreground",
          )}
        >
          {isPageLayout ? "Sign in" : "Login"}
        </CardTitle>
        <CardDescription className={isPageLayout ? "text-sm" : "sr-only"}>
          {isPageLayout ? "Use your Clarimed credentials to access messaging." : "Authentication controls"}
        </CardDescription>
      </CardHeader>
      <CardContent className={cn(isPageLayout ? "p-6 pt-5" : "p-3 pt-0")}>
        <div className={cn("space-y-2", isPageLayout && "space-y-3")}>
          <Input
            type="text"
            value={form.loginId}
            onChange={(event) => form.setLoginId(event.target.value)}
            placeholder="email"
            disabled={isLoggedIn || form.isSubmitting}
            className={cn(isPageLayout ? "h-11" : "h-9")}
          />
          {!isLoggedIn ? (
            <Input
              type="password"
              value={form.password}
              onChange={(event) => form.setPassword(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void form.submit();
                }
              }}
              placeholder="password"
              disabled={form.isSubmitting}
              className={cn(isPageLayout ? "h-11" : "h-9")}
            />
          ) : null}
        </div>
        <div className={cn("flex gap-2", isPageLayout ? "mt-5" : "mt-3")}>
          <Button
            type="button"
            onClick={() => void form.submit()}
            disabled={isLoggedIn || form.isSubmitting}
            size={isPageLayout ? "default" : "sm"}
            className={isPageLayout ? "min-w-30" : undefined}
          >
            {form.isSubmitting ? "Signing in…" : "Login"}
          </Button>
          <Button
            type="button"
            onClick={handleLogout}
            disabled={!isLoggedIn}
            variant="outline"
            size={isPageLayout ? "default" : "sm"}
          >
            Logout
          </Button>
        </div>
      </CardContent>
      <LoginErrorDialog
        open={form.errorDialog.open}
        kind={form.errorDialog.kind}
        onOpenChange={(open) => form.setErrorDialog((prev) => ({ ...prev, open }))}
      />
    </Card>
  );

  return (
    <>
      {showRoster ? (
        <div className={className}>
          {/* Above the tiles: this is the thing that may sign you in before you touch one, so it
              should be the first thing you look at. Self-hides when face unlock is off. */}
          <FaceSignInStatus className="mb-4" />
          <ProfileRoster
            profiles={profiles}
            onPick={handlePickProfile}
            onRemove={(profile) => removeProfile(profile.userId)}
            onUseCredentials={() => setShowCredentialsCard(true)}
          />
          <LoginErrorDialog
            open={form.errorDialog.open}
            kind={form.errorDialog.kind}
            onOpenChange={(open) => form.setErrorDialog((prev) => ({ ...prev, open }))}
          />
        </div>
      ) : (
        <>
          {credentialsCard}
          {/* Way back to the roster tiles: the card is reached from a tile mis-tap or the
              "another account" link, and without this the roster is unreachable until
              someone signs in. */}
          {isPageLayout && !isLoggedIn && profiles.length > 0 && showCredentialsCard ? (
            <div className="mt-3 text-center">
              <Button
                type="button"
                variant="link"
                size="sm"
                className="h-auto p-0 text-xs"
                onClick={() => setShowCredentialsCard(false)}
              >
                Back to all profiles
              </Button>
            </div>
          ) : null}
        </>
      )}
      {isPageLayout ? (
        <PinEntryDialog
          open={pinEntry.open}
          onOpenChange={(open) => setPinEntry((prev) => ({ ...prev, open }))}
          profile={pinEntry.profile}
          onUsePassword={(profile) => {
            form.setLoginId(profile?.loginId || "");
            setShowCredentialsCard(true);
          }}
        />
      ) : null}
    </>
  );
}

export default LoginPanel;
